import {
  animate,
  Animator,
  ImageCoverView,
  View,
} from '@play-co/timestep-core/ui';
import * as config from './uiConfig';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import sounds from 'src/lib/sounds';
import StateObserver from 'src/StateObserver';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { getFriendsPet, getPet } from 'src/game/pet';
import { getPlayerIdFromConversation } from 'src/game/conversation/conversation.utils';
import { EntityManager } from 'src/game/logic/EntityManager';

import { AssetGroup } from '@play-co/timestep-core';

export type SpeechBubbleCloseHandler<Result> = (result?: Result) => void;

export default abstract class PopupSpeechBubbleBase<
  Result = any,
> extends PopupBasic<SpeechBubbleCloseHandler<Result>> {
  protected tail: ImageCoverView;
  protected tapIcon: ImageCoverView;
  protected currentAnimation: Animator | null = null;
  protected closeAfterSkip: boolean;

  constructor(opts: {
    superview: View;
    close: SpeechBubbleCloseHandler<Result>;
    $subFeature: string;
    closeAfterSkip?: boolean;
  }) {
    super({
      boxOpts: config.box,
      ...config.popup,
      ...opts,
      zIndex: 10001,
      tracking: { feature: 'popup', $subFeature: opts.$subFeature },
    });

    this.bg.updateOpts(config.bg);
    this.buttonClose.removeFromSuperview();
    this.message.updateOpts(config.message);

    this.tail = new ImageCoverView({
      superview: this.box,
      ...config.tail,
    });

    this.tapIcon = new ImageCoverView({
      superview: this.box,
      ...config.tapIcon,
    });

    this.bg.onClick = this.handleClick.bind(this);
    this.closeAfterSkip = opts.closeAfterSkip ?? false;
  }

  protected getAssetsGroup() {
    return new AssetGroup([]);
  }

  public init(opts: any) {
    super.init(opts);

    const playerId = getPlayerIdFromConversation(opts.id);

    this.message.updateOpts(
      playerId !== platform.playerID ? config.messageFriend : config.message,
    );

    const { position } =
      playerId === platform.playerID ? getPet() : (getFriendsPet() ?? {});

    if (!position) {
      throw new Error(
        'Trying to show speech bubble without position, pet is possibly undefined',
      );
    }

    const offset = EntityManager.getCurrentEntitiesOffset();

    const location = position.getSceneLocation();
    this.box.style.y +=
      config.getYOffset(opts.move_to ?? location) + offset.offsetY;

    if (playerId) {
      this.tail.updateOpts(
        config.getTailPositionOpts(opts.move_to ?? location, playerId),
      );
    }
  }

  public async fadeIn() {
    const originalMessage = this.message.text ?? '';
    this.message.text = '';

    await super.fadeIn();

    const charactersPerSecond = 8;

    this.currentAnimation?.commit();
    this.currentAnimation = animate(this.message)
      .commit()
      .then(() => {
        this.tapIcon.hide();
      })
      .then(
        (value) => {
          const originalLength = this.message?.text?.length ?? 0;

          this.message.text = originalMessage.slice(
            0,
            Math.floor(originalMessage.length * value),
          );

          const currentLength = this.message.text.length;

          if (currentLength === originalLength) return;

          sounds.sfx.pause('speak');

          if ([' ', '\n', '.'].includes(this.message.text[currentLength - 1])) {
            return;
          }

          sounds.sfx.play('speak');
        },
        1000 * (originalMessage.length / charactersPerSecond),

        animate.linear,
      )
      .then(() => {
        this.tapIcon.show();
        this.currentAnimation = null;
        sounds.sfx.stop('speak');
        sounds.sfx.play('period');
      });
  }

  private handleClick() {
    if (this.currentAnimation != null) {
      this.currentAnimation.commit();
      return;
    }

    if (!this.closeAfterSkip) return;

    sounds.sfx.play('ok');
    return this.opts.close();
  }
}
